import { motion } from 'framer-motion'
import { Shield, Flame, Heart, Sparkles, ArrowRight } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { Link } from 'react-router-dom'

const parts = [
  {
    icon: Shield,
    name: 'Managers',
    description: 'Try to control life to keep you safe. They plan, worry, and push you to stay ahead of criticism or failure.',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  {
    icon: Flame,
    name: 'Firefighters',
    description: 'Leap in when things feel overwhelming — often with impulsive distractions like overworking, scrolling, or numbing out.',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
  },
  {
    icon: Heart,
    name: 'Exiles',
    description: 'The tender, wounded parts that carry past pain and vulnerability — often hidden away to protect you.',
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-200 dark:border-violet-800',
  },
  {
    icon: Sparkles,
    name: 'The Self',
    description: 'The calm, compassionate, curious center within you. The inner leader capable of bringing harmony to the whole system.',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
]

const applications = [
  'Identify what\'s driving procrastination, perfectionism, people-pleasing, or indecision',
  'Discover the values different parts carry — and update the ones that no longer serve you',
  'Unblend from parts that are running the show, so the Self can lead',
  'Align your inner system so your energy is focused on what matters most',
]

export default function IFSPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Page header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            The Framework
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            What is Internal Family Systems?
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            A practical map for understanding and working with the competing voices in your inner world.
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* Intro */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-3xl mb-16">
          <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-5">
            We all have inner conflicts — moments when one part of us wants change and another resists.
            Internal Family Systems (IFS) offers a practical map for understanding and working with those inner contradictions.
          </p>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            Developed by Dr. Richard Schwartz, IFS is not just a therapeutic model — it's a framework for self-leadership.
            It assumes that every part of you has a positive intention, even when its behavior seems harmful or counterproductive.
          </p>
        </motion.div>

        {/* Parts grid */}
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">The Parts of Your Inner System</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {parts.map(({ icon: Icon, name, description, color, bg, border }, i) => (
            <motion.div key={name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`rounded-2xl border p-6 flex flex-col gap-4 ${bg} ${border}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
                <Icon size={20} className={color} />
              </div>
              <h3 className={`font-bold text-lg ${color}`}>{name}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{description}</p>
            </motion.div>
          ))}
        </div>

        {/* IFS in coaching */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">IFS in Coaching</h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-5">
              IFS is widely used in therapy — and for good reason. But in coaching, we use it differently.
              We're not digging into trauma (that's therapy's job). Instead, we work with your inner protectors to create forward momentum.
            </p>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              This work is especially powerful for midlife professionals who've already done some inner work but still feel stuck.
              If you've tried therapy and made progress but still ask, <em>"Why am I not where I want to be?"</em> — IFS coaching may be the missing link.
            </p>
            <ul className="space-y-3 mb-8">
              {applications.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300 text-sm">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                    <ArrowRight size={11} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
              Explore IFS Coaching
            </CalendlyBtn>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="bg-slate-900 rounded-3xl p-8 text-white">
            <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-4">In Practice</p>
            <p className="text-slate-200 leading-relaxed mb-5 text-lg font-light italic">
              "Let's say part of you wants to start a business, but another part is afraid of failure.
              Instead of pushing through or giving up, we'd explore each part's concerns."
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              We'd hear their perspectives, validate their intentions, and invite cooperation. When your parts feel seen and safe,
              they stop pulling in opposite directions — and you move forward with more confidence and less friction.
            </p>
            <div className="border-t border-white/10 pt-6">
              <p className="text-slate-300 text-sm mb-5">
                You don't need to be fixed. You need to be understood — by yourself, first.
              </p>
              <Link to="/blog" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
                Read articles on IFS <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-3">Experience IFS coaching firsthand</h2>
          <p className="text-slate-400 mb-8">Start with a free consultation to see if this work is right for you.</p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book Free Consultation
          </CalendlyBtn>
        </div>
      </div>
    </div>
  )
}
